// ─────────────────────────────────────────────────────────────────────────────
// ListPriceObservationsComponent
// ─────────────────────────────────────────────────────────────────────────────
// Paginated price-observations table with search, filters, sort, checkbox
// selection, bulk delete, per-row delete, and the two resolution actions.
// Responsibilities:
//   - Dispatch LoadPriceObservations and display the paginated result
//   - Debounced keyword search (product key / designation / customer code)
//   - Filter dialog: customer, state, scope, import
//   - Server-side column sorting (default: create_date desc -- the newest row,
//     so a fresh import lands at the top whatever its price dates)
//   - Resolve the customer of an unresolved row, and the product of a
//     to-verify row — each one also teaches the backend registry, so the same
//     sender / the same code resolves by itself at the next import
//   - Open the price-source import dialog and reload on success
//
// Two rules are visible in the UI on purpose:
//   - a `conflict` row publishes nothing (two prices quoted the same day), and
//     the operator picks which one survives;
//   - an `unresolved` row is never served to the agent, whatever its price.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, getCurrencySymbol } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest, shareReplay } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { DATE_FORMAT, DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { StatusBadgeTone } from 'src/app/ui/status-badge/status-badge.component';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { FilterDialogComponent } from 'src/app/shared/components/filter-dialog/filter-dialog.component';
import { FilterDialogData, FilterFieldOption } from 'src/app/shared/components/filter-dialog/filter-dialog.models';

import {
  EMPTY_PRICE_OBSERVATION_FILTERS as EMPTY_FILTERS,
  IPriceObservation,
  IPriceObservationFilters,
  PRICE_OBSERVATION_SCOPES,
  PRICE_OBSERVATION_SOURCE_KINDS,
  PRICE_OBSERVATION_STATUSES,
  candidateLabel,
  productLabel,
  sourceLabel,
} from '../../models/price-observation.models';
import { KnowledgeBaseService } from '../../services/knowledge-base.service';
import { PriceObservationService } from '../../services/price-observation.service';
import { PriceSourcePreviewService } from 'src/app/shared/file-preview-dialog/price-source-preview.service';
import {
  PriceObservationEditDialogComponent,
  PriceObservationEditDialogData,
  PriceObservationEditDialogResult,
} from '../price-observation-edit-dialog/price-observation-edit-dialog.component';
import { ImportKnowledgeBaseDialogComponent } from '../import-knowledge-base-dialog/import-knowledge-base-dialog.component';
import {
  selectPriceObservationConflictCount,
  selectPriceObservationItems,
  selectPriceObservationLoading,
  selectPriceObservationMetadata,
  selectPriceObservationToVerifyCount,
  selectPriceObservationUnresolvedCount,
} from '../../store/price-observations.selectors';
import {
  BulkDeletePriceObservations,
  BulkDeletePriceObservationsSuccess,
  DeletePriceObservation,
  DeletePriceObservationSuccess,
  KeepPriceObservation,
  KeepPriceObservationSuccess,
  LoadPriceObservations,
  SetPriceObservationFilters,
  SetPriceObservationSearch,
  UpdatePriceObservation,
  UpdatePriceObservationSuccess,
} from '../../store/price-observations.actions';
import { ProductsService } from 'src/app/features/produits/services/produits.services';

type SortableField = 'observed_at' | 'price' | 'state' | 'scope' | 'create_date';

@Component({
  selector: 'app-list-price-observations',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './list-price-observations.component.html',
  styleUrls: ['./list-price-observations.component.scss'],
})
export class ListPriceObservationsComponent implements OnInit, OnDestroy {
  // ── Store selectors ───────────────────────────────────────────────────────

  observations$: Observable<IPriceObservation[]> = this.store.select(selectPriceObservationItems);
  metadata$: Observable<IPagination | null> = this.store.select(selectPriceObservationMetadata);
  private readonly awaitingImport$ = new BehaviorSubject<boolean>(false);
  /**
   * The store's own loading, or the wait for a deferred extraction.
   *
   * The upload route answers 202 and the prices land seconds later, so without
   * this the table shows the state from before the import and reads as "the
   * import did nothing".
   */
  loading$: Observable<boolean> = combineLatest([
    this.store.select(selectPriceObservationLoading),
    this.awaitingImport$,
  ]).pipe(
    map(([storeLoading, awaiting]) => storeLoading || awaiting),
    // Three `| async` in the template, one subscription: without it each pipe
    // re-runs the combineLatest on every store emission.
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  unresolvedCount$: Observable<number> = this.store.select(selectPriceObservationUnresolvedCount);
  toVerifyCount$: Observable<number> = this.store.select(selectPriceObservationToVerifyCount);
  conflictCount$: Observable<number> = this.store.select(selectPriceObservationConflictCount);

  // ── Local state ───────────────────────────────────────────────────────────

  DATE_FORMAT = DATE_FORMAT;

  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  keyword = '';
  filters: Partial<IPriceObservationFilters> = { ...EMPTY_FILTERS };
  /**
   * Whether anything narrows the table, read five times by the toolbar and the
   * empty state. A field and not a getter: `filters` only ever changes through
   * `applyFilters`, and as a method it re-tested ten keys on every change
   * detection pass, five times over.
   */
  hasActiveFilters = false;
  /**
   * A Set, not an array: `isSelected` is called once per row on every change
   * detection pass, and `includes` on 25 rows made that quadratic on a screen
   * where every checkbox click triggers a pass.
   */
  selected = new Set<number>();
  /**
   * What the table opens on: the newest ROW, not the newest price date.
   *
   * `observed_at desc` put a freshly imported file wherever its price dates
   * fell — a tariff dated last month landed mid-table and the import read as
   * "nothing happened". `create_date` is the one key an import moves, and it
   * is the backend's own default order. The date column stays sortable.
   */
  sortField: SortableField = 'create_date';
  sortMode: 'asc' | 'desc' = 'desc';
  rows: IPriceObservation[] = [];
  searchControl = new FormControl('');
  activeFilterValues: Record<string, any> = {};
  isExporting = false;

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private customersService: CustomersService,
    private productsService: ProductsService,
    private priceObservationService: PriceObservationService,
    private sourcePreview: PriceSourcePreviewService,
    private knowledgeBaseService: KnowledgeBaseService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.observations$.pipe(takeUntil(this.destroy$)).subscribe((rows) => {
      this.rows = [...rows];
    });

    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.onSearch(value ?? '');
    });

    // Reload after anything that WRITES. A correction is not local to its row:
    // the server re-runs the merge, so the rival of a conflict changes state
    // too, the row may leave the filter that is showing it, and the header
    // counters are aggregates no client-side arithmetic can keep true — the
    // reducer's own patch used a narrower rule than the backend's and the
    // counts drifted until the operator reloaded the page.
    this.actions$
      .pipe(
        ofType(
          BulkDeletePriceObservationsSuccess,
          DeletePriceObservationSuccess,
          KeepPriceObservationSuccess,
          UpdatePriceObservationSuccess,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.selected.clear();
        this.loadObservations();
      });

    // Deleting the last row of the last page must step back one page, not
    // strand the operator on an empty screen without pagination.
    this.metadata$.pipe(takeUntil(this.destroy$)).subscribe((metadata) => {
      if (metadata && metadata.pages > 0 && this.page.number > metadata.pages) {
        this.page = { ...this.page, number: metadata.pages };
        this.loadObservations();
      }
    });

    this.loadObservations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadObservations(): void {
    this.store.dispatch(
      LoadPriceObservations({
        page: this.page,
        key: this.keyword || undefined,
        filters: this.filters,
        sortBy: this.sortField || undefined,
        sortMode: this.sortField ? this.sortMode : undefined,
      }),
    );
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.selected.clear();
    this.loadObservations();
  }

  // ── Search ────────────────────────────────────────────────────────────────

  onSearch(value: string): void {
    this.keyword = value;
    this.selected.clear();
    this.store.dispatch(SetPriceObservationSearch({ keyword: value }));
    this.page = { ...this.page, number: 1 };
    this.loadObservations();
  }

  // ── Quick filters (the tab header counters are clickable) ────────────────

  /**
   * The chips are SHORTCUTS that fill the status filter — they never clear
   * the other filters. Clicking the active chip clears just itself.
   */
  filterByStatus(status: IPriceObservationFilters['status']): void {
    const active = this.filters.status === status && !this.filters.state;
    this.filters = { ...this.filters, status: active ? null : status, state: null };
    this.applyFilters();
  }

  /** The conflict chip: the "À vérifier" rows that carry a conflict. */
  filterByConflict(): void {
    const active = this.filters.state === 'conflict';
    this.filters = { ...this.filters, state: active ? null : 'conflict', status: null };
    this.applyFilters();
  }

  /** Show the observations of one import — the link the import journal opens. */
  filterByImport(importId: number): void {
    this.filters = { ...EMPTY_FILTERS, import_id: importId };
    this.applyFilters();
  }

  private applyFilters(): void {
    this.hasActiveFilters = Object.values(this.filters).some((value) => !!value);
    this.store.dispatch(SetPriceObservationFilters({ filters: this.filters }));
    this.page = { ...this.page, number: 1 };
    this.selected.clear();
    this.loadObservations();
  }

  // ── Filter dialog ─────────────────────────────────────────────────────────

  private get filterConfig(): FilterDialogData {
    return {
      config: {
        title: 'knowledge_base.price_observations.filter.title',
        sections: [
          {
            title: 'knowledge_base.price_observations.filter.group_title',
            fields: [
              {
                key: 'customer',
                label: 'filter.customer',
                type: 'autocomplete',
                placeholder: 'filter.customer_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.customersService
                    .listCustomers({ number: 1, size: 20 }, undefined, term)
                    .pipe(map((res) => (res.data ?? []).map((c) => ({ value: c.id, label: c.name })))),
              },
              {
                // ONE status field: the raw state would be a second vocabulary
                // for the same rows, and is deliberately not offered.
                key: 'status',
                label: 'knowledge_base.price_observations.col.status',
                type: 'single-select',
                options: this.optionsFor('status', PRICE_OBSERVATION_STATUSES),
              },
              {
                key: 'product',
                label: 'knowledge_base.price_observations.col.product',
                type: 'autocomplete',
                placeholder: 'knowledge_base.price_observations.filter.product_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.productsService
                    .fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, term, undefined, false, true)
                    .pipe(
                      map((res) =>
                        (res.data ?? [])
                          .filter((product) => product.product_template_id != null || product.id != null)
                          .map((product) => ({
                            value: (product.product_template_id ?? product.id)!,
                            label: product.sku ? `${product.sku} — ${product.name}` : product.name,
                          })),
                      ),
                    ),
              },
              {
                key: 'source_kind',
                label: 'knowledge_base.price_observations.col.source_kind',
                type: 'single-select',
                options: this.optionsFor('source_kind', PRICE_OBSERVATION_SOURCE_KINDS),
              },
              {
                // The two bounds each need their OWN key: without minKey /
                // maxKey both inputs write to the same slot and the filter
                // never reaches the server.
                key: 'observed',
                label: 'knowledge_base.price_observations.col.observed_at',
                type: 'date-range',
                minKey: 'observed_from',
                maxKey: 'observed_to',
              },
              {
                key: 'scope',
                label: 'knowledge_base.price_observations.col.scope',
                type: 'single-select',
                options: this.optionsFor('scope', PRICE_OBSERVATION_SCOPES),
              },
              {
                // Unchecked by default — checked, "Remplacé" would always be
                // empty.
                key: 'is_current',
                label: 'knowledge_base.price_observations.filter.current_only',
                type: 'checkbox',
              },
            ],
          },
        ],
      },
      values: {},
    };
  }

  /**
   * The options of one single-select filter.
   *
   * The values come from the model's own list, so a value added to the union
   * appears here without anyone remembering to add it; only the i18n key is
   * built by convention, and a missing key is visible on screen.
   */
  private optionsFor(group: string, values: readonly string[]): FilterFieldOption[] {
    return values.map((value) => ({ value, label: this.label(group, value) }));
  }

  setFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: { ...this.filterConfig, values: { ...this.activeFilterValues } } as FilterDialogData,
      width: '900px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: Record<string, any> | undefined) => {
        if (!result) return;
        this.activeFilterValues = result;
        this.filters = {
          ...EMPTY_FILTERS,
          customer_id: result['customer']?.value ?? null,
          product_id: result['product']?.value ?? null,
          scope: result['scope'] || null,
          status: result['status'] || null,
          source_kind: result['source_kind'] || null,
          is_current: result['is_current'] ? true : null,
          observed_from: result['observed_from'] || null,
          observed_to: result['observed_to'] || null,
        };
        this.applyFilters();
      });
  }

  /**
   * Export what is on screen, not the whole table.
   *
   * The current filters are sent, and the backend reuses the very function
   * the list filters with. An export that re-derived its own filter would be
   * a second answer to "which rows are Expired".
   */
  exportObservations(): void {
    this.isExporting = true;
    this.priceObservationService
      .exportObservations(this.searchControl.value || undefined, this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExporting = false;
          this.knowledgeBaseService.triggerBlobDownload(blob, 'price_observations.xlsx');
        },
        error: () => {
          this.isExporting = false;
        },
      });
  }

  clearFilter(): void {
    this.activeFilterValues = {};
    this.filters = { ...EMPTY_FILTERS };
    this.applyFilters();
  }

  // ── Sorting (server-side) ─────────────────────────────────────────────────

  onSort(field: SortableField): void {
    if (this.sortField === field) {
      this.sortMode = this.sortMode === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortMode = 'asc';
    }
    this.page = { ...this.page, number: 1 };
    this.loadObservations();
  }

  // ── Checkbox selection ────────────────────────────────────────────────────

  get isAllSelected(): boolean {
    return this.rows.length > 0 && this.selected.size === this.rows.length;
  }

  get isPartialSelected(): boolean {
    return this.selected.size > 0 && this.selected.size < this.rows.length;
  }

  toggleAll(checked: boolean): void {
    this.selected = new Set(checked ? this.rows.map((row) => row.id) : []);
  }

  toggleOne(id: number, checked: boolean): void {
    if (checked) this.selected.add(id);
    else this.selected.delete(id);
  }

  isSelected(id: number): boolean {
    return this.selected.has(id);
  }

  trackById(_index: number, row: IPriceObservation): number {
    return row.id;
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  deleteRow(row: IPriceObservation): void {
    this.confirm(() => this.store.dispatch(DeletePriceObservation({ id: row.id })));
  }

  bulkDelete(): void {
    this.confirm(() => this.store.dispatch(BulkDeletePriceObservations({ ids: [...this.selected] })));
  }

  private confirm(action: () => void): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) action();
      });
  }

  // ── The resolution loop ───────────────────────────────────────────────────

  /** An unresolved customer is the operator's first job on this table. */
  needsCustomer(row: IPriceObservation): boolean {
    return row.scope === 'unresolved';
  }

  /** No product means the row cannot be served at all, whatever its price. */
  needsProduct(row: IPriceObservation): boolean {
    return row.state === 'to_verify' || !row.product_id;
  }

  isConflict(row: IPriceObservation): boolean {
    return row.state === 'conflict';
  }

  /**
   * Remplacé and Doublon are read-only. The server refuses the edit anyway —
   * the screen simply does not offer it.
   */
  isLocked(row: IPriceObservation): boolean {
    return row.status === 'superseded' || row.status === 'duplicate';
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  /**
   * The amount AS IT IS — never re-rounded — plus the SYMBOL, the same render
   * as the tooltip. The ISO code is the fallback when Angular knows no symbol
   * for it, the one case where the code is the clearest thing to show.
   */
  displayPrice(row: IPriceObservation): string {
    if (!row.currency) return `${row.price}`;
    return `${row.price} ${getCurrencySymbol(row.currency, 'narrow')}`;
  }

  /**
   * The unit as the document wrote it — with the basis ONLY when it warns.
   *
   * `unit_basis` is empty when the source did not say whether the unit is the
   * base or a selling one; showing the raw unit alone is the honest render.
   * When it IS known, only `selling_unit` changes how the price reads: a unit
   * that multiplies the base is what makes two amounts incomparable. Spelling
   * out `base_unit` said nothing — on a file writing the unit "Unit" it
   * rendered "Unit (base unit)", a tautology wrapped over two lines in the
   * narrowest column of the table. The basis moved to the tooltip, where it
   * answers the question without costing a row its height.
   */
  displayUnit(row: IPriceObservation): string {
    if (!row.unit) return '—';
    return row.unit_basis === 'selling_unit'
      ? `${row.unit} (${this.unitBasisLabel(row)})`
      : row.unit;
  }

  /** The basis spelled out, for the cell's tooltip. Empty when unknown. */
  unitBasisLabel(row: IPriceObservation): string {
    return row.unit_basis ? this.label('unit_basis', row.unit_basis) : '';
  }

  displayCustomer(row: IPriceObservation): string {
    return row.customer_name ?? row.counterparty_email ?? '—';
  }

  /** A global tariff is FOR everyone — a dash would read as "no data". */
  isGlobalAudience(row: IPriceObservation): boolean {
    return row.scope === 'global';
  }

  isGroupAudience(row: IPriceObservation): boolean {
    return row.scope === 'group';
  }

  groupNames(row: IPriceObservation): string {
    return (row.group_names ?? []).join(', ');
  }

  displayProduct = productLabel;

  /**
   * The matcher's best candidate, next to the raw text the file wrote.
   *
   * It replaces a "Not recognised" tag that repeated the raw code and the
   * status pill without ever saying which product the matcher thinks it is.
   * Empty for a row that has a product, or one the matcher had no answer for.
   */
  topSuggestionLabel(row: IPriceObservation): string {
    if (row.product_id) return '';
    const candidate = row.match_candidates?.[0];
    if (!candidate) return '';
    return candidateLabel(candidate);
  }

  displaySource = sourceLabel;

  /**
   * Show the document this price was read from.
   *
   * The row could always NAME its source; naming it is not evidence. This
   * opens the body of the message, or the attached document, or the
   * spreadsheet — whichever one the line actually came from.
   */
  openSource(row: IPriceObservation, event: Event): void {
    event.stopPropagation();
    if (!row.document_id) return;
    this.sourcePreview.open({ documentId: row.document_id, label: this.displaySource(row) });
  }

  /**
   * The edit dialog: client, product, price, dates and scope, editable even
   * on an attached row. The correction writes IN PLACE, so the id in the
   * response is the id in the store and the reducer patches the row where it
   * stands.
   */
  onEditClick(row: IPriceObservation): void {
    const dialogRef = this.dialog.open(PriceObservationEditDialogComponent, {
      data: { observation: row } as PriceObservationEditDialogData,
      // 700px, not 640: app-shared-dialog renders at its `medium` size, which
      // writes width:700px INLINE on its own container. A 640px panel around
      // a 700px container clipped the right column -- currency, valid until,
      // and the SAVE button -- outside the panel, unreachable. Same width the
      // import dialog opens with, for the same wrapper size.
      width: '700px',
      // Like the import dialog -- without it a narrow window renders the
      // form past the viewport, with the confirm button off screen.
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PriceObservationEditDialogResult | null) => {
        if (!result) return;
        if (result.action === 'delete') {
          this.confirm(() => this.store.dispatch(DeletePriceObservation({ id: row.id })));
          return;
        }
        if (result.action === 'keep-price') {
          this.store.dispatch(KeepPriceObservation({ id: row.id }));
          return;
        }
        if (result.payload && Object.keys(result.payload).length) {
          // The dialog has already asked whatever had to be asked — the
          // external reference among it — and answered inside its own payload.
          this.store.dispatch(UpdatePriceObservation({ id: row.id, payload: result.payload }));
        }
      });
  }

  /**
   * One value of one enum, translated by the SAME convention `optionsFor`
   * builds its filter options with — so a filter option and the badge it
   * filters on can never end up reading two different keys.
   */
  private label(group: string, value: string): string {
    return this.translateService.instant(`knowledge_base.price_observations.${group}.${value}`);
  }

  /**
   * The one status label, derived server-side and rendered as given:
   * re-deriving it here is a second formula for "expired", which is how a row
   * reads Active in the list and Expired in the tooltip.
   *
   * No fallback on `state`: `display_status` always answers and `status` is
   * always serialised, so the fallback is unreachable.
   */
  statusLabel(row: IPriceObservation): string {
    return this.label('status', row.status);
  }

  /**
   * The tone of the status pill, for `<ui-status-badge>`.
   *
   * The list painted its own pill in hand-written rgba, one shade per status,
   * while the rest of the app resolves the same six tones from
   * `$status-badge-palette`. Two greens for "this is fine" is one too many.
   */
  statusTone(row: IPriceObservation): StatusBadgeTone {
    // A conflict is carried by `state`, never by the derived status — and it
    // is the one thing the pill must not paint like anything else: two prices
    // for one day, neither published.
    if (this.isConflict(row)) return 'volta';
    switch (row.status) {
      case 'active':
        return 'success';
      case 'expired':
      case 'unknown_customer':
        return 'destructive';
      case 'to_verify':
        return 'alert';
      default:
        return 'draft';
    }
  }

  /**
   * What the status pill explains on hover.
   *
   * One pill, one tooltip: a conflict says neither price is published, an
   * unresolved row that it is never served to the agent. Carried by separate
   * badges in the same cell, they shrank the pill itself to "Unk...".
   */
  statusTooltip(row: IPriceObservation): string {
    if (this.isConflict(row)) {
      return this.label('state', 'conflict_tooltip');
    }
    if (this.needsCustomer(row)) {
      return this.label('scope', 'unresolved_tooltip');
    }
    return '';
  }

  /** The weak channel stays visible: a price resolved by domain says so. */
  resolvedByLabel(row: IPriceObservation): string {
    return this.label('resolved_by', row.resolved_by);
  }

  isWeakAttribution(row: IPriceObservation): boolean {
    return row.resolved_by === 'domain';
  }

  /**
   * The candidates beside the one already named, or 0.
   *
   * The list shows the matcher's best answer by name and the row has to say the
   * others exist, but a "3 suggestions" badge says only how many while taking
   * the room the name needs. `+2` says it on the proposal itself.
   *
   * Zero on a line that already has a product, whatever the matcher proposed.
   */
  otherSuggestionCount(row: IPriceObservation): number {
    if (row.product_id) return 0;
    return Math.max((row.match_candidates?.length ?? 0) - 1, 0);
  }

  // ── Import ────────────────────────────────────────────────────────────────

  openImportDialog(): void {
    const dialogRef = this.dialog.open(ImportKnowledgeBaseDialogComponent, {
      data: { type: 'price-observation' },
      disableClose: true,
      width: '820px',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: number[] | boolean) => {
        if (!result) return;
        if (!Array.isArray(result)) {
          this.loadObservations();
          return;
        }
        // 202: the journal rows exist, the observations do not yet. Hold the
        // spinner until the extraction has run, then load once — reloading
        // now would paint the pre-import table.
        this.awaitingImport$.next(true);
        this.priceObservationService
          .awaitImports(result)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.awaitingImport$.next(false);
              this.loadObservations();
            },
            error: () => {
              // The wait failed, the import did not: load what there is and
              // let the journal tab tell the rest.
              this.awaitingImport$.next(false);
              this.loadObservations();
            },
          });
      });
  }
}
